import { create } from 'zustand';
import { toast } from 'react-hot-toast';
import { apiCall } from '@/services/api';

export interface CustomFieldKv {
  key: string;
  value: string;
}

export interface CatalogItem {
  id: string;
  tenant_id: string;
  loja_id: string | null;
  tipo: 'produto' | 'servico';
  nome: string;
  sku: string | null;
  descricao: string | null;
  imagens: string[];
  preco: number;
  moeda: string;
  tamanho: string | null;
  cores: string | null;
  condicoes_pagamento: string | null;
  link_produto: string | null;
  entrega: string | null;
  link_pagamento: string | null;
  custom_fields: CustomFieldKv[];
  ativo: boolean;
  em_estoque?: boolean;
  quantidade_estoque?: number;
  controla_estoque?: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  loja?: { id: string; nome: string };
}

interface CatalogState {
  items: CatalogItem[];
  loading: boolean;
  catalogEnabled: boolean;
  configFetched: boolean;
  fetchConfig: () => Promise<void>;
  toggleCatalog: (enabled: boolean) => Promise<boolean>;
  fetchItems: (filters?: { tipo?: string; loja_id?: string; q?: string; active_only?: boolean }) => Promise<void>;
  createItem: (data: Partial<CatalogItem>) => Promise<CatalogItem | null>;
  updateItem: (id: string, updates: Partial<CatalogItem>) => Promise<boolean>;
  deleteItem: (id: string) => Promise<boolean>;
  uploadImage: (itemId: string, file: File) => Promise<string | null>;
  removeImage: (itemId: string, url: string) => Promise<boolean>;
}

export const useCatalogStore = create<CatalogState>((set, get) => ({
  items: [],
  loading: false,
  catalogEnabled: false,
  configFetched: false,

  fetchConfig: async () => {
    try {
      const res = await apiCall('/api/catalog/config');
      if (res.ok) {
        const data = await res.json();
        set({
          catalogEnabled: !!data.catalog_enabled,
          configFetched: true
        });
      }
    } catch (err) {
      console.error('[CatalogStore] Erro ao buscar config:', err);
    }
  },

  toggleCatalog: async (enabled: boolean) => {
    try {
      const res = await apiCall('/api/catalog/config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ catalog_enabled: enabled })
      });

      if (res.ok) {
        const data = await res.json();
        set({
          catalogEnabled: !!data.catalog_enabled,
          configFetched: true
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('[CatalogStore] Erro ao alterar status do catálogo:', err);
      return false;
    }
  },

  fetchItems: async (filters) => {
    set({ loading: true });
    try {
      const queryParams = new URLSearchParams();
      if (filters?.tipo && filters.tipo !== 'todos') {
        queryParams.append('tipo', filters.tipo);
      }
      if (filters?.loja_id) {
        queryParams.append('loja_id', filters.loja_id);
      } else {
        queryParams.append('loja_id', 'all');
      }
      if (filters?.q && filters.q.trim()) {
        queryParams.append('q', filters.q.trim());
      }
      if (filters?.active_only) {
        queryParams.append('active_only', 'true');
      }

      const queryString = queryParams.toString();
      const url = `/api/catalog/items${queryString ? `?${queryString}` : ''}`;

      const res = await apiCall(url);
      if (res.ok) {
        const data = await res.json();
        set({ items: Array.isArray(data) ? data : [], loading: false });
      } else {
        set({ loading: false });
      }
    } catch (err) {
      console.error('[CatalogStore] Erro ao buscar itens do catálogo:', err);
      set({ loading: false });
    }
  },

  createItem: async (data) => {
    try {
      const res = await apiCall('/api/catalog/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        const created: CatalogItem = await res.json();
        set((state) => ({ items: [created, ...state.items] }));
        toast.success('Item criado com sucesso!');
        return created;
      }
      const errData = await res.json().catch(() => ({}));
      const errorMsg = errData.error || errData.message || `Erro ${res.status} ao criar item.`;
      toast.error(errorMsg);
      console.error('[CatalogStore] Erro ao criar item no catálogo:', errorMsg);
      return null;
    } catch (err: any) {
      const msg = err?.message || 'Erro de conexão ao criar item.';
      toast.error(msg);
      console.error('[CatalogStore] Erro ao criar item no catálogo:', err);
      return null;
    }
  },

  updateItem: async (id, updates) => {
    try {
      const res = await apiCall(`/api/catalog/items/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        const updated: CatalogItem = await res.json();
        set((state) => ({
          items: state.items.map((item) => (item.id === id ? updated : item))
        }));
        toast.success('Item atualizado com sucesso!');
        return true;
      }
      const errData = await res.json().catch(() => ({}));
      const errorMsg = errData.error || errData.message || `Erro ${res.status} ao atualizar item.`;
      toast.error(errorMsg);
      console.error('[CatalogStore] Erro ao atualizar item no catálogo:', errorMsg);
      return false;
    } catch (err: any) {
      const msg = err?.message || 'Erro de conexão ao atualizar item.';
      toast.error(msg);
      console.error('[CatalogStore] Erro ao atualizar item no catálogo:', err);
      return false;
    }
  },

  deleteItem: async (id) => {
    try {
      const res = await apiCall(`/api/catalog/items/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id)
        }));
        toast.success('Item excluído com sucesso!');
        return true;
      }
      const errData = await res.json().catch(() => ({}));
      const errorMsg = errData.error || errData.message || `Erro ${res.status} ao excluir item.`;
      toast.error(errorMsg);
      return false;
    } catch (err: any) {
      toast.error('Erro de conexão ao excluir item.');
      console.error('[CatalogStore] Erro ao deletar item no catálogo:', err);
      return false;
    }
  },

  uploadImage: async (itemId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await apiCall(`/api/catalog/items/${itemId}/upload-image`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json(); // { url, imagens }
        if (data.imagens) {
          set((state) => ({
            items: state.items.map((item) =>
              item.id === itemId ? { ...item, imagens: data.imagens } : item
            )
          }));
        }
        return data.url || null;
      }
      const errData = await res.json().catch(() => ({}));
      const errorMsg = errData.error || errData.message || `Erro (${res.status}) ao enviar arquivo.`;
      toast.error(errorMsg);
      return null;
    } catch (err: any) {
      const msg = err?.message || 'Erro de conexão ao enviar arquivo.';
      toast.error(msg);
      console.error('[CatalogStore] Erro ao fazer upload de imagem do item:', err);
      return null;
    }
  },

  removeImage: async (itemId, url) => {
    try {
      const res = await apiCall(`/api/catalog/items/${itemId}/image`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });

      if (res.ok) {
        const data = await res.json(); // { success: true, imagens }
        if (data.imagens) {
          set((state) => ({
            items: state.items.map((item) =>
              item.id === itemId ? { ...item, imagens: data.imagens } : item
            )
          }));
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('[CatalogStore] Erro ao remover imagem do item:', err);
      return false;
    }
  }
}));
